/**
 * Fallas: traducirlas y dejar rastro.
 *
 * Dos trabajos distintos que suelen confundirse.
 *
 * **Traducir** es para quien está parado frente a la pantalla. «Missing or
 * insufficient permissions» no le sirve a nadie en una caja: lo que necesita
 * saber es que no tiene permiso y a quién pedírselo. Un mensaje que no dice qué
 * hacer es ruido con apariencia de información.
 *
 * **Dejar rastro** es para después. Cuando alguien llama diciendo «no me dejó
 * cobrar» y ya pasaron tres horas, la pantalla se cerró y nadie copió nada, la
 * única forma de saber qué pasó es haberlo anotado en el momento. Por eso las
 * fallas se guardan con quién, dónde y cuándo.
 *
 * El registro es **siempre best-effort**: si falla, se calla. Una falla al
 * anotar una falla no puede tumbar lo que el usuario estaba haciendo, y una
 * cadena de errores intentando registrar errores es cómo una aplicación se
 * cuelga sola.
 */

import { db } from './firebase';
import { empresaDeRuta, rutaLogica } from './ruta';

/** Lo que dice Firestore, y lo que de verdad hay que hacer al respecto. */
const POR_CODIGO: Record<string, string> = {
  'permission-denied':
    'No tienes permiso para esto. Si crees que deberías tenerlo, pídeselo a quien administre la plataforma.',
  unauthenticated: 'Se cerró tu sesión. Vuelve a entrar.',
  unavailable: 'Sin conexión con el servidor. Revisa la señal e intenta de nuevo.',
  'deadline-exceeded': 'El servidor se demoró más de la cuenta. Intenta otra vez.',
  'not-found': 'Eso ya no existe. Quizá alguien lo borró mientras lo tenías abierto.',
  'already-exists': 'Ya existe algo con ese identificador.',
  'failed-precondition':
    'La operación no se pudo hacer con el estado actual. Recarga la pantalla y vuelve a intentar.',
  aborted: 'Otra persona cambió lo mismo al tiempo. Recarga y vuelve a intentar.',
  'resource-exhausted': 'Se pasó el límite de consultas. Espera un momento.',
  cancelled: 'La operación se canceló antes de terminar.',

  // Entrar y cambiar contraseña. Viven aquí y no sueltos en la pantalla de
  // entrar porque un error de sesión puede salir en cualquier parte: se vence
  // el token a media tarde y la falla aparece guardando un pedido, no entrando.
  'auth/invalid-email': 'Ese correo no parece válido.',
  'auth/invalid-credential': 'Correo o contraseña incorrectos.',
  'auth/wrong-password': 'Correo o contraseña incorrectos.',
  'auth/user-not-found': 'Correo o contraseña incorrectos.',
  'auth/user-disabled': 'Esta cuenta está desactivada. Habla con el administrador.',
  'auth/too-many-requests': 'Demasiados intentos. Espera un momento y vuelve a probar.',
  'auth/network-request-failed': 'Sin conexión. Revisa tus datos o el wifi.',
  'auth/weak-password': 'La contraseña nueva es muy corta. Mínimo seis caracteres.',
  'auth/requires-recent-login': 'Por seguridad, vuelve a entrar y repite el cambio.',
  'auth/expired-action-code': 'Ese enlace ya venció. Pide uno nuevo.',
  'auth/invalid-action-code': 'Ese enlace ya se usó o no es válido. Pide uno nuevo.',
};

const POR_TEXTO: { busca: RegExp; dice: string }[] = [
  {
    busca: /Unsupported field value: undefined/i,
    dice: 'Un dato quedó sin valor y no se pudo guardar. Es un error de la aplicación, no tuyo — avísale a quien la mantiene.',
  },
  {
    // Pasa siempre igual: alguien deja la pestaña abierta, se publica una
    // versión nueva, y al abrir una pantalla que todavía no había bajado, el
    // archivo que pide ya no está. No es una falla del usuario ni del servidor,
    // y se arregla recargando — pero sin este mensaje se ve como un bloqueo.
    busca: /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i,
    dice: 'Se publicó una versión nueva mientras tenías esto abierto. Recarga la página y sigue donde ibas.',
  },
  { busca: /Failed to fetch|NetworkError|network request failed/i,
    dice: 'Sin conexión. Revisa la señal e intenta de nuevo.' },
  { busca: /quota/i, dice: 'Se pasó el límite del servicio. Espera un momento.' },
];

/** El mensaje que se le muestra a una persona. Siempre en español y accionable. */
export function mensajeDeFallo(e: unknown, respaldo = 'No se pudo completar. Intenta de nuevo.'): string {
  const codigo = (e as { code?: string } | null)?.code;
  if (codigo && POR_CODIGO[codigo]) return POR_CODIGO[codigo];

  const texto = e instanceof Error ? e.message : String(e ?? '');
  for (const { busca, dice } of POR_TEXTO) if (busca.test(texto)) return dice;

  /*
   * Un error marcado como **nuestro** se muestra tal cual, siempre.
   *
   * Debajo hay además una heurística —«empieza en mayúscula y termina en
   * punto, luego parece escrito para una persona»— que se quedó corta y costó
   * una tarde: la pantalla de facturación decía «No se pudo consultar el
   * emisor» y nada más, mientras el servicio estaba devolviendo la causa
   * exacta. El texto venía del facturador, no pasaba el filtro, y se perdía.
   *
   * El problema de fondo de adivinar si un mensaje es legible es que falla en
   * silencio y del lado malo: ante la duda esconde información que alguien
   * necesitaba. La marca `propio` no adivina — la pone quien lanza el error,
   * que es el único que sabe si su texto está escrito para leerse.
   *
   * La heurística se queda para los errores de terceros que sí vienen en
   * español, y como red para el código que todavía no marca los suyos.
   */
  if ((e as { propio?: boolean } | null)?.propio && texto) return texto;

  const pareceEscrito = e instanceof Error
    && /^[A-ZÁÉÍÓÚÑ¿][^]*[.?]$/.test(texto)
    && !/[a-z]+\(\)|undefined|null/.test(texto);
  return pareceEscrito ? texto : respaldo;
}

/** Para no escribir cien veces la misma falla en un bucle. */
const yaVistas = new Set<string>();

/**
 * Anota la falla, sin estorbar.
 *
 * Guarda lo necesario para reconstruir qué pasó: el mensaje técnico, en qué
 * pantalla, con qué usuario y en qué empresa. No guarda lo que el usuario
 * estaba escribiendo — eso puede tener datos de un cliente, y un registro de
 * diagnóstico no es lugar para ellos.
 */
export async function registrarFallo(
  e: unknown,
  contexto: { donde: string; uid?: string; extra?: string },
): Promise<void> {
  try {
    const texto = e instanceof Error ? `${e.name}: ${e.message}` : String(e ?? '');
    const llave = `${contexto.donde}|${texto}`;
    if (yaVistas.has(llave)) return;
    yaVistas.add(llave);

    const empresa = empresaDeRuta();
    if (!empresa) return;

    const fs = await import('firebase/firestore');
    const base = await db();
    await fs.addDoc(fs.collection(base, 'empresas', empresa, 'diagnostico'), {
      mensaje: texto.slice(0, 1000),
      codigo: (e as { code?: string } | null)?.code ?? '',
      donde: contexto.donde,
      ruta: rutaLogica(),
      extra: contexto.extra ?? '',
      uid: contexto.uid ?? '',
      agente: navigator.userAgent.slice(0, 200),
      en_linea: navigator.onLine,
      creado_en: fs.serverTimestamp(),
      creado_local: Date.now(),
    });
  } catch {
    // A propósito en silencio: una falla al anotar una falla no puede tumbar
    // lo que la persona estaba haciendo.
  }
}

/** Traduce, anota y devuelve el mensaje. Es lo que se llama desde un catch. */
export function manejar(
  e: unknown,
  contexto: { donde: string; uid?: string; extra?: string },
  respaldo?: string,
): string {
  console.error(contexto.donde, e);
  void registrarFallo(e, contexto);
  return mensajeDeFallo(e, respaldo);
}
